/* Create App — Stage 0 of the build journey: ONE action, and the app
 * already runs (bootstrap births the identity source, seed roles, the
 * first admin user, auth workflows and predefined journeys).
 * The app id is ENGINE-MINTED (app_<12hex>) — there is no id input; the
 * success state surfaces the minted id because it is the tenancy key AND
 * the /app/<id> runtime URL. */
import React, {
    useState
} from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle
} from 'shared/components/ui/dialog';
import {
    CheckCircle2,
    Loader2,
    Lock,
    Sparkles
} from 'lucide-react';
import {
    appEngineService,
    describeError
} from '../api/appEngine.service';
import {
    openAppUrl
} from '../shared/openAppUrl';
import {
    notify
} from 'shared/api/notifier';

const BIRTH_KIT = [
    'App document (the eight pointer facts)',
    'Identity source with its capability catalog',
    'Seed roles: anonymous · admin · default role',
    'First admin user (the creator)',
    'Auth workflows: wf_sign_in · wf_sign_up',
    'Journeys: sign_in · sign_up · home (+ admin pair)'
];

function slugify(s: string): string {
    return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** A cold org's FIRST bootstrap seeds identity into its (cross-region) Mongo,
 * which can outlast the proxy/ingress timeout (502/504) even though BeXO
 * completes the create and the app appears. So when the create response is
 * lost, poll the app list for the new app — an id that wasn't there before. */
async function pollForNewApp(
    orgId: string, before: Set<string>, timeoutMs: number
): Promise<string | null> {
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
        await sleep(3000);
        try {
            const apps = await appEngineService.listApps(orgId);
            const fresh = apps.find((a) => a.id && !before.has(a.id) && !a.demo);
            if (fresh) return fresh.id;
        } catch { /* transient — keep polling */ }
    }
    return null;
}

type Props = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    orgId: string;
    onCreated: (appId: string) => void;
};

export default function CreateAppDialog({
    open,
    onOpenChange,
    orgId,
    onCreated
}: Props) {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [creatorName, setCreatorName] = useState('');
    const [defaultRole, setDefaultRole] = useState('member');
    /* Creator roles are deterministic chips, not free text: 'admin' is
     * LOCKED on (the first user must be able to manage roles) and the
     * default role rides along by default — matching the API default
     * of [admin, default_role] when both chips are on. */
    const [withDefaultRole, setWithDefaultRole] = useState(true);
    const [busy, setBusy] = useState(false);
    const [provisioning, setProvisioning] = useState(false);
    const [bornAppId, setBornAppId] = useState<string | null>(null);
    const [creatorRoles, setCreatorRoles] = useState<string[] | null>(null);

    const roleId = defaultRole.trim();
    const valid = name.trim() && email.trim() && password && roleId;

    const submit = async () => {
        if (!valid || busy) return;
        setBusy(true);
        // Snapshot existing app ids so a slow create whose response is lost to a
        // proxy timeout can still be confirmed (the new app will appear).
        let before: Set<string> = new Set();
        try {
            before = new Set((await appEngineService.listApps(orgId)).map((a) => a.id));
        } catch { /* best-effort snapshot */ }

        const payload = {
            name: name.trim(),
            org: orgId,
            creator: {
                email: email.trim(),
                password,
                name: creatorName.trim() || undefined,
                roles: withDefaultRole && roleId !== 'admin'
                    ? ['admin', roleId]
                    : ['admin']
            },
            default_role: roleId
        };

        try {
            const res = await appEngineService.bootstrap(orgId, payload);
            const minted = res?.app_id ? String(res.app_id) : '';
            if (minted) {
                setCreatorRoles(Array.isArray(res?.creator_roles)
                    ? res.creator_roles.map(String)
                    : null);
                setBornAppId(minted);
                setBusy(false);
                return;
            }
            // 200 without an app_id — confirm via the list (treat as a timeout).
        } catch (err: any) {
            // Only a GATEWAY timeout (502/504) or no response at all can mean
            // "BeXO is still finishing" — a cold org's cross-region identity
            // seed can outlast the proxy. Any definitive status (500/503/400/…)
            // is a real failure: surface it and stop (don't poll a doomed
            // create). bootstrap is skip-listed from the global error toast, so
            // this is the single place that reports a create failure.
            const status = err?.response?.status;
            const timedOut = !err?.response || status === 502 || status === 504;
            if (!timedOut) {
                notify(describeError(err), 'danger');
                setBusy(false);
                return;
            }
        }

        setProvisioning(true);
        const minted = await pollForNewApp(orgId, before, 120000);
        setProvisioning(false);
        setBusy(false);
        if (minted) {
            setBornAppId(minted);
        } else {
            notify('Create is taking longer than usual — it may still complete; '
                + 'refresh the apps list in a moment.', 'warning');
        }
    };

    const reset = (o: boolean) => {
        if (!o) {
            setBornAppId(null);
            setCreatorRoles(null);
        }
        onOpenChange(o);
    };

    return (
        <Dialog open={open} onOpenChange={reset}>
            <DialogContent className="sm:max-w-[480px]">
                {bornAppId ? (
                    <>
                        <DialogHeader>
                            <DialogTitle style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8
                            }}>
                                <CheckCircle2 size={20} color="var(--aps-success)" />
                                {name || bornAppId} is alive
                            </DialogTitle>
                            <DialogDescription>
                                The bootstrap generated the full birth-kit. Every piece is an
                                ordinary editable document — the same loop edits them all.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="aps-card" style={{
                            padding: '12px 16px',
                            margin: '2px 0 4px'
                        }}>
                            <p style={{
                                margin: 0,
                                fontSize: 'var(--aps-text-xs)',
                                color: 'var(--aps-text-muted)',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.04em'
                            }}>
                                Minted app id — the tenancy key
                            </p>
                            <p className="aps-mono" style={{
                                margin: '4px 0 6px',
                                fontSize: 'var(--aps-text-lg)',
                                fontWeight: 700,
                                color: 'var(--aps-text)'
                            }}>
                                {bornAppId}
                            </p>
                            <p className="aps-mono" style={{
                                margin: 0,
                                fontSize: 'var(--aps-text-xs)',
                                color: 'var(--aps-text-2)',
                                wordBreak: 'break-all'
                            }}>
                                {openAppUrl(orgId, bornAppId)}
                            </p>
                            {creatorRoles && creatorRoles.length > 0 && (
                                <div className="aps-chip-row" style={{
                                    marginTop: 10
                                }}>
                                    <span className="aps-meta">Creator roles</span>
                                    {creatorRoles.map((r) => (
                                        <span key={r} className="aps-badge aps-badge-indigo">
                                            {r}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                        <ul style={{
                            margin: '4px 0 8px',
                            paddingLeft: 20,
                            fontSize: 'var(--aps-text-sm)',
                            color: 'var(--aps-text-2)',
                            lineHeight: 1.9
                        }}>
                            {BIRTH_KIT.map((b) => <li key={b}>{b}</li>)}
                        </ul>
                        <button className="aps-btn aps-btn-primary" onClick={() => onCreated(bornAppId)}>
                            Open {bornAppId} in the studio
                        </button>
                    </>
                ) : (
                    <>
                        <DialogHeader>
                            <DialogTitle style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8
                            }}>
                                <Sparkles size={18} color="var(--aps-primary)" />
                                Create App
                            </DialogTitle>
                            <DialogDescription>
                                One action — when it completes, the creator can already
                                sign in as admin to an empty home.
                            </DialogDescription>
                        </DialogHeader>
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 12
                        }}>
                            <div>
                                <label className="aps-label">App name</label>
                                <input className="aps-input" value={name} placeholder="Trainee Portal"
                                    onChange={(e) => setName(e.target.value)} />
                                <p className="aps-help">
                                    Display name only — the app id is minted by the engine on create.
                                </p>
                            </div>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: 10
                            }}>
                                <div>
                                    <label className="aps-label">Creator email</label>
                                    <input className="aps-input" type="email" value={email}
                                        placeholder="you@org.com"
                                        onChange={(e) => setEmail(e.target.value)} />
                                </div>
                                <div>
                                    <label className="aps-label">Creator password</label>
                                    <input className="aps-input" type="password" value={password}
                                        onChange={(e) => setPassword(e.target.value)} />
                                </div>
                            </div>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: 10
                            }}>
                                <div>
                                    <label className="aps-label">Creator name</label>
                                    <input className="aps-input" value={creatorName} placeholder="optional"
                                        onChange={(e) => setCreatorName(e.target.value)} />
                                </div>
                                <div>
                                    <label className="aps-label">Default role</label>
                                    <input className="aps-input aps-input-mono" value={defaultRole}
                                        onChange={(e) => setDefaultRole(slugify(e.target.value))} />
                                    <p className="aps-help">
                                        Assigned to new users at sign-up
                                        (via $.context.app.default_role).
                                    </p>
                                </div>
                            </div>
                            <div>
                                <label className="aps-label">Creator roles</label>
                                <div className="aps-chip-row">
                                    <button type="button" className="aps-filter-chip active" disabled
                                        title={'The first user must hold admin — '
                                            + 'otherwise no one can manage roles'}
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 5,
                                            cursor: 'not-allowed',
                                            background: 'var(--aps-primary)',
                                            borderColor: 'var(--aps-primary)',
                                            color: 'var(--aps-text-on-primary)',
                                            transform: 'none'
                                        }}>
                                        <Lock size={11} />
                                        admin
                                    </button>
                                    {roleId && roleId !== 'admin' && (
                                        <button type="button"
                                            className={'aps-filter-chip'
                                                + (withDefaultRole ? ' active' : '')}
                                            onClick={() => setWithDefaultRole((v) => !v)}>
                                            {roleId}
                                        </button>
                                    )}
                                </div>
                                <p className="aps-help">
                                    The first user must hold admin — otherwise no one
                                    can manage roles.
                                </p>
                            </div>
                            <button className="aps-btn aps-btn-primary" disabled={!valid || busy}
                                onClick={submit}>
                                {busy
                                    ? <Loader2 size={15} className="aps-spin" />
                                    : <Sparkles size={15} />}
                                {provisioning
                                    ? 'Provisioning… (a new org can take ~a minute)'
                                    : busy ? 'Bootstrapping…' : 'Create — and it runs'}
                            </button>
                        </div>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
